// =============================================================================
// 5.44.0 "HealthSherpa in use" lock — the shared store (one per page) and the transfer helper.
// HealthSherpa allows ONE agent login at a time; going there from InsureDocs takes a 10-minute lock everyone sees.
// - Status: GET /api/hs-lock/status every POLL_MS while a page is open, on window focus, and right after every lock
//   action. The server decides everything (expiry is computed there on read); the clock offset to the server is kept
//   so countdowns don't depend on this computer's clock.
// - transfer(url): MUST be called straight from the click (popup blockers): a blank window opens synchronously, then
//   the claim; OK -> that window goes to HealthSherpa; 409 -> it closes and the "in use" modal shows (HsLockStrip).
//   The holder clicking another transfer: claim answers OK without changing the end time.
// =============================================================================
import { hsLockApi } from './api';

export const POLL_MS = 15000;
const ACK_KEY = 'insuredocs.hsLock.ackNotice.v1';
let state = { status: null, offset: 0, blocked: null, error: null };
const listeners = new Set();

export const getState = () => state;
function set(patch) { state = { ...state, ...patch }; listeners.forEach((fn) => { try { fn(state); } catch (_e) { /* ignore */ } }); }
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

function take(data) {
  if (!data || typeof data !== 'object' || !('active' in data)) return;
  const offset = data.server_now ? Date.parse(data.server_now) - Date.now() : 0;
  set({ status: data, offset: Number.isFinite(offset) ? offset : 0 });
}

export async function refresh() {
  try {
    if (!localStorage.getItem('token')) return;
    const r = await hsLockApi.status();
    take(r.data);
  } catch (_e) { /* silent: keep the last state */ }
}

let users = 0;
let timer = null;
const onFocus = () => refresh();
const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
export function startPolling() {
  users += 1;
  if (users === 1) {
    refresh();
    timer = setInterval(refresh, POLL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
  }
  return () => {
    users -= 1;
    if (users === 0) {
      clearInterval(timer); timer = null;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    }
  };
}

export const serverNow = () => Date.now() + (state.offset || 0);
export function msLeft(st = state.status) {
  if (!st || !st.active || !st.ends_at) return 0;
  return Math.max(0, Date.parse(st.ends_at) - serverNow());
}

function blankWindow() {
  let w = null;
  try {
    w = window.open('', '_blank');
    if (w && w.document) {
      w.document.title = 'Opening HealthSherpa…';
      w.document.body.style.cssText = 'font-family: sans-serif; color: #555; padding: 24px';
      w.document.body.textContent = 'Opening HealthSherpa…';
    }
  } catch (_e) { /* blocked */ }
  return w;
}

function go(w, url) {
  if (w && !w.closed) {
    try { w.opener = null; } catch (_e) { /* ignore */ }
    w.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function busyFrom(e, url) {
  const d = e?.response?.data || {};
  return { holder: d.holder || null, ends_label: d.ends_label || null, ends_at: d.ends_at || null, url };
}

// Called synchronously from a click on any "go to HealthSherpa" button. Returns a promise (true = opened).
export function transfer(url, onOpened) {
  const w = blankWindow();
  return hsLockApi.claim().then((r) => {
    take(r.data);
    go(w, url);
    if (onOpened) onOpened();
    return true;
  }).catch((e) => {
    try { if (w) w.close(); } catch (_e) { /* ignore */ }
    if (e?.response?.status === 409) { set({ blocked: { ...busyFrom(e, url), onOpened } }); refresh(); }
    else set({ error: e?.response?.data?.detail || 'Could not check whether HealthSherpa is free — try again' });
    return false;
  });
}

// The modal's "Override" — also straight from the click (it opens HealthSherpa).
export function overrideAndOpen(url, onOpened) {
  const w = blankWindow();
  return hsLockApi.override().then((r) => {
    take(r.data); set({ blocked: null });
    go(w, url);
    if (onOpened) onOpened();
    return true;
  }).catch((e) => {
    try { if (w) w.close(); } catch (_e) { /* ignore */ }
    set({ error: e?.response?.data?.detail || 'Could not take over — try again' });
    refresh();
    return false;
  });
}

export const closeBlocked = () => set({ blocked: null });
export const clearError = () => set({ error: null });

async function act(fn) {
  try { const r = await fn(); take(r.data); return { ok: true }; }
  catch (e) {
    const d = e?.response?.data || {};
    refresh();
    return { ok: false, status: e?.response?.status, detail: d.detail || 'Something went wrong', holder: d.holder || null, ends_label: d.ends_label || null };
  }
}
export const done = () => act(() => hsLockApi.done());
export const extend = (minutes) => act(() => hsLockApi.extend(minutes));

// The previous holder's notice (time ran out / taken over / cleared by an admin) — dismissed per notice, per browser.
export function noticeAcked(n) {
  if (!n) return true;
  try { return localStorage.getItem(ACK_KEY) === n.key; } catch (_e) { return false; }
}
export function ackNotice(n) {
  try { if (n) localStorage.setItem(ACK_KEY, n.key); } catch (_e) { /* ignore */ }
  set({ status: state.status ? { ...state.status } : state.status });
}

export function minutesLeftLabel(ms) {
  const m = Math.ceil(ms / 60000);
  return m <= 1 ? 'less than a minute' : `about ${m} min`;
}
export function mmss(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
